import {computed, onScopeDispose, Ref, shallowRef, toRaw, watch} from 'vue'
import {useRouter} from 'vue-router'
import {useDocumentVisibility} from '@vueuse/core'
import {useLocalMessageStore} from '@toolify/client/src/stores/LocalMessageStore/useLocalMessageStore'
import {LocalMessageStoreEventType} from '@toolify/client/src/stores/LocalMessageStore/enum/LocalMessageStoreEventType'
import {ILocalMessage} from '@toolify/client/src/modules/notifications/types/ILocalMessage'
import {IFloatingLocalMessageSyncItem} from '@toolify/client/src/modules/notifications/types/IFloatingLocalMessageSyncItem'
import {MessageStateType} from '@toolify/client/src/modules/notifications/enum/MessageStateType'
import {MessageActionKindType} from '@toolify/client/src/modules/notifications/enum/MessageActionKindType'
import {MessageDismissReasonType} from '@toolify/client/src/modules/notifications/enum/MessageDismissReasonType'
import {isNativeLocalMessageCandidate} from '@toolify/client/src/modules/notifications/functions/isNativeLocalMessageCandidate'
import {mapLocalMessageToFloatingSyncItem} from '@toolify/client/src/modules/notifications/functions/mapLocalMessageToFloatingSyncItem'
import {FLOATING_STACK_MAX_VISIBLE} from '@toolify/client/src/modules/notifications/functions/computeFloatingStackLayout'

interface IUseNativeLocalMessagesOptions {
  // true once the desktop app has answered; until then nothing goes native
  isEnabled: Ref<boolean>
  // Windows: the floating notification window shows the cards; elsewhere a system notification does
  isInSeparateWindow: Ref<boolean>
  // the desktop app brings its window to the front
  onSystemNotificationClick: (messageId: string) => void
}

const DISMISS_REASONS = new Set<string>(Object.values(MessageDismissReasonType))
const NO_FLOATING_SYNC_ITEMS: IFloatingLocalMessageSyncItem[] = []

// Desktop app: a local message raised while the app window is hidden (minimized, closed to the tray) goes
// native, the app's own stack cannot be seen then. Only what happens while hidden goes, so the feedback to
// a click ("Copied") never does. Visible again, the app shows its own cards (their timers stood still) and
// the native copies are dropped.
export function useNativeLocalMessages(options: IUseNativeLocalMessagesOptions) {
  const router = useRouter()
  const localMessageStore = useLocalMessageStore()
  const documentVisibility = useDocumentVisibility()
  const nativeMessageIds = shallowRef<ReadonlySet<string>>(new Set())
  const systemNotificationsById = new Map<string, Notification>()
  // a push that reuses the id of a card that has left is a new card, so messages are told apart by object
  const seenMessages = new WeakSet<ILocalMessage>()
  localMessageStore.getMessages().forEach(message => seenMessages.add(toRaw(message)))

  const nativeMessages = computed(() => {
    return localMessageStore.getMessages().filter(message => {
      return message.state !== MessageStateType.Leave && nativeMessageIds.value.has(message.id)
    })
  })

  const floatingSyncItems = computed<IFloatingLocalMessageSyncItem[]>(() => {
    if(!options.isInSeparateWindow.value || !nativeMessages.value.length) {
      return NO_FLOATING_SYNC_ITEMS
    }
    const shownFlowMessages = new Set(nativeMessages.value
      .filter(message => !message.pinned)
      .sort((first, second) => first.createdAt - second.createdAt || first.seqNo - second.seqNo)
      .slice(-FLOATING_STACK_MAX_VISIBLE))
    return nativeMessages.value
      .filter(message => message.pinned || shownFlowMessages.has(message))
      .map(mapLocalMessageToFloatingSyncItem)
  })

  function findLiveMessage(id: string): ILocalMessage | undefined {
    return localMessageStore.getMessages().find(message => message.id === id && message.state !== MessageStateType.Leave)
  }

  function closeSystemNotification(id: string) {
    systemNotificationsById.get(id)?.close()
    systemNotificationsById.delete(id)
  }

  function closeAllSystemNotifications() {
    systemNotificationsById.forEach(notification => notification.close())
    systemNotificationsById.clear()
  }

  function showSystemNotification(message: ILocalMessage) {
    if(typeof Notification === 'undefined' || Notification.permission !== 'granted') {
      return
    }
    const title = message.title || message.text
    if(!title) {
      return
    }
    closeSystemNotification(message.id)
    const notification = new Notification(title, {
      body: message.title ? message.text : undefined,
      // an update of the same message replaces its notification instead of adding another one
      tag: message.id,
      silent: true,
    })
    notification.onclick = () => options.onSystemNotificationClick(message.id)
    notification.onclose = () => {
      if(systemNotificationsById.get(message.id) === notification) {
        systemNotificationsById.delete(message.id)
      }
    }
    systemNotificationsById.set(message.id, notification)
  }

  // a push, a morph or a ×N bump
  function onMessageRaised(message: ILocalMessage, isBump: boolean) {
    if(!options.isEnabled.value || documentVisibility.value === 'visible' || !isNativeLocalMessageCandidate(message)) {
      return
    }
    const isAlreadyNative = nativeMessageIds.value.has(message.id)
    if(!isAlreadyNative) {
      nativeMessageIds.value = new Set([...nativeMessageIds.value, message.id])
    }
    // the window follows every change through floatingSyncItems; a system notification only comes for new content
    if(options.isInSeparateWindow.value || (isAlreadyNative && isBump)) {
      return
    }
    showSystemNotification(message)
  }

  watch(() => localMessageStore.getMessages(), messages => {
    const messageIds = new Set<string>()
    messages.forEach(message => {
      messageIds.add(message.id)
      const rawMessage = toRaw(message)
      if(!seenMessages.has(rawMessage)) {
        seenMessages.add(rawMessage)
        onMessageRaised(message, false)
      }
    })
    const nativeIds = [...nativeMessageIds.value]
    const removedIds = nativeIds.filter(id => !messageIds.has(id))
    if(removedIds.length) {
      removedIds.forEach(closeSystemNotification)
      nativeMessageIds.value = new Set(nativeIds.filter(id => messageIds.has(id)))
    }
  }, {flush: 'sync'})

  const unsubscribeFromStore = localMessageStore.subscribeToEvents(event => {
    if(event.type === LocalMessageStoreEventType.Tick) {
      return
    }
    const message = findLiveMessage(event.id)
    if(message) {
      onMessageRaised(message, event.type === LocalMessageStoreEventType.Bump)
    }
  })

  watch(documentVisibility, visibility => {
    if(visibility !== 'visible') {
      return
    }
    closeAllSystemNotifications()
    if(nativeMessageIds.value.size) {
      nativeMessageIds.value = new Set()
    }
  })

  // Closed in the notification window (×, timeout, action): the app card goes too, it has been seen.
  // Once the app is visible again its cards are its own, so a late report is ignored.
  function dismiss(id: string, reason: string) {
    if(nativeMessageIds.value.has(id) && DISMISS_REASONS.has(reason)) {
      localMessageStore.dismiss(id, reason as MessageDismissReasonType)
    }
  }

  // The card's own link handling, then the store runs the callbacks; a navigating action has already
  // brought the app window to the front
  function runAction(id: string, actionIndex: number) {
    const action = findLiveMessage(id)?.actions[actionIndex]
    if(!action) {
      return
    }
    if(action.to) {
      router.push(action.to)
    } else if(action.kind === MessageActionKindType.Link && action.href && action.href !== '#') {
      window.open(action.href, '_blank', 'noopener')
    }
    localMessageStore.runAction(id, actionIndex)
  }

  onScopeDispose(() => {
    unsubscribeFromStore()
    closeAllSystemNotifications()
  })

  return {
    floatingSyncItems,
    dismiss,
    runAction,
  }
}
